import { Box, Button, Typography } from '@mui/material';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as MinusIcon } from '../../../../assets/svg/minus.svg';
import { ReactComponent as PlusIcon } from '../../../../assets/svg/plus.svg';
import { LANG_TEXT } from '../../../../constants/language';
import { useUserMenuContext } from '../../../../context/UserMenuContext';
import { DamagedItemStatus } from '../../../../interfaces/index.types';
import { formatPriceItem } from '../../../../utils';
import { IFoodMenus } from '../../../MenuManagement/menu.types';
import { IFood, IOrders, ITopic } from '../../order.types';
import { useStyles } from '../DrawerOrderItem/styles';

interface FoodItemProps {
  food: IFood;
  foodMenu: IFoodMenus;
  order: IOrders;
  quantity: number;
  onQuantityChange: (item_food_id: string, quantity: number) => void;
  maxItem: number;
  cancellation_order: boolean;
  partialCancellation: boolean;
}

const FoodItem: React.FC<FoodItemProps> = ({ food, foodMenu, order, quantity, onQuantityChange, maxItem, cancellation_order, partialCancellation }) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const handleIncrement = () => {
    if (quantity < maxItem) {
      onQuantityChange(food.item_food_id, quantity + 1);
    }
  };

  const handleDecrement = () => {
    if (quantity > 0) {
      onQuantityChange(food.item_food_id, quantity - 1);
    }
  };

  const { getFoodById } = useUserMenuContext();

  const foodDetail = useMemo(() => {
    return foodMenu.foods.find((item) => item.id === food.item_food_id) || getFoodById(food.item_food_id);
  }, [foodMenu.foods, food.item_food_id, getFoodById]);

  const imageUrl = useMemo(() => {
    if (!foodDetail?.image_url) return '';
    return Array.isArray(foodDetail.image_url) ? foodDetail.image_url[0] : foodDetail.image_url;
  }, [foodDetail]);

  return (
    <Box
      id={`food-${food.item_food_id}`}
      sx={{
        borderBottom: '1px solid var(--Gray-300, #D0D5DD)',
      }}
      className={classes.BoxContentOrderSumary}
    >
      <Box className={classes.ContentOrderSumary}>
        <Box className={classes.BoxPriceFood}>
          <img
            src={imageUrl}
            height={100}
            width={100}
            style={{
              borderRadius: '8px',
            }}
            alt="food"
          />
          <Typography fontSize={16} fontWeight={600} lineHeight={'24px'} color={'var(--Gray-700, #344054)'}>
            ¥ {formatPriceItem(food.item_price)}
          </Typography>
        </Box>
        <Box className={classes.BoxInformationFood}>
          <Typography fontSize={20} fontWeight={600} lineHeight={'30px'} color={'var(--Gray-800, #1D2939)'}>
            {food.item_name}
          </Typography>
          {food.item_customize_settings?.length > 0 && (
            <Box className={classes.BoxStarFood}>
              {food.item_customize_settings.map((topic: ITopic, index: number) => (
                <Box className={classes.BoxStarFood} key={index}>
                  <Typography fontSize={16} fontWeight={500} lineHeight={'24px'}>
                    - {topic.name}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
          {food.item_note && (
            <Typography fontSize={16} fontWeight={600} lineHeight={'24px'} color={'var(--Gray-800, #667085)'}>
              {food.item_note}
            </Typography>
          )}
          <Typography fontSize={14} lineHeight={'20px'} color={'var(--Primary-700, #1D2939)'}>
            {t(LANG_TEXT.QUANTITY)}: {food.item_deleted_quantity !== undefined ? food.item_quantity - food.item_deleted_quantity : food.item_quantity}{' '}
            {t(LANG_TEXT.ITEMS)}
          </Typography>
          {((!cancellation_order && partialCancellation) || order.order_status === DamagedItemStatus.DAMAGED_CREATED) && (
            <Box className={classes.BoxDamaged}>
              <Typography fontSize={14} lineHeight={'20px'} color={'var(--Primary-700, #1D2939)'}>
                {!cancellation_order && partialCancellation ? t(LANG_TEXT.ACTUAL_REFUND_QUANTITY) : t(LANG_TEXT.ACTUAL_DAMAGE_QUANTITY)}:
              </Typography>
              <Box className={classes.QuantityBox}>
                <Button type="button" onClick={handleDecrement} className={classes.QuantityButton}>
                  <MinusIcon className={classes.QuantityIcon} />
                </Button>
                <Typography padding={1} fontSize={16} lineHeight={'24px'} color={'var(--Primary-700, #1D2939)'}>
                  {quantity}
                </Typography>
                <Button type="button" onClick={handleIncrement} className={classes.QuantityButton}>
                  <PlusIcon className={classes.QuantityIcon} />
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default FoodItem;
